import {
    Button,
    Form,
    Headline,
    InputControl,
    Loading,
    Picker,
    RequiredText,
    Spacer,
    StyleFunction,
    TextInput,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import { SUBMIT_DEcomponents } from "fiskaly";
import {
    GQFiskalyTaxPayerLegal,
    GQFiskalyTaxPayerType,
    useUpsertFiskalyTaxPayerLegalMutation,
} from "graphql-sdk";
import { useTranslation } from "locales";
import { useCallback } from "react";
import React, { View } from "react-native";
import { Divider } from "react-native-paper";
import { useHandleMutationError } from "../../../../hooks";
import {
    schemaTaxPayerLegalForm,
    FiskalyTaxPayerLegalFormType,
    defaultTaxPayerLegalForm,
} from "../fiskaly-form";

export function LegalTaxPayerWidget({
    taxPayer,
}: {
    taxPayer: GQFiskalyTaxPayerLegal;
}) {
    const [t] = useTranslation();
    const [upsertLegalTaxPayer] = useUpsertFiskalyTaxPayerLegalMutation();
    const { handleMutationError } = useHandleMutationError();
    const form = useForm(
        schemaTaxPayerLegalForm,
        (taxPayer as FiskalyTaxPayerLegalFormType) || defaultTaxPayerLegalForm
    );

    const [{ values }] = form;

    const save = useCallback(async () => {
        if (!values) {
            return;
        }

        await handleMutationError(async () => {
            await upsertLegalTaxPayer({
                variables: {
                    taxPayerData: {
                        companyName: values.companyName,
                        type: GQFiskalyTaxPayerType.Legal,
                        webAddress: values.webAddress,
                        taxNumber: values.taxNumber,
                        taxOfficeNumber: values.taxOfficeNumber,
                        vatNumber: values.vatNumber,
                        legalForm: values.legalForm,

                        street: values.street,
                        houseNumber: values.houseNumber,
                        houseNumberSuffix: values.houseNumberSuffix,
                        zip: values.zip,
                        town: values.town,
                        country: "DE",
                    },
                },
            });
        }, t("common.created", "Created"));
    }, [handleMutationError, t, upsertLegalTaxPayer, values]);

    if (!values) {
        return <Loading />;
    }

    return <TaxPayerLegalForm form={form} onSubmit={save} />;
}

// We don't want to try and translate these names/titles/legal forms. They are only used in Germany.
// The values and labels are from the Fiskaly API: https://developer.fiskaly.com/api/sign-de/submission/v1#tag/Taxpayer/operation/UpsertTaxpayer
// Look at field: general_information --> field: information field --> type: LegalPersonDetailedInformation --> field: legal_form
const legalForms: { label: string; value: number }[] = [
    { value: 31, label: "Aktiengesellschaft" },
    { value: 25, label: "Aktiengesellschaft u. Co. KG" },
    { value: 26, label: "Aktiengesellschaft u. Co. OHG" },
    { value: 49, label: "Andere Erwerbs- oder Wirtschaftsgenossenschaft" },
    { value: 20, label: "Atypische stille Gesellschaft" },
    { value: 91, label: "Ausländische Körperschaft" },
    { value: 92, label: "Ausländische Personengesellschaft" },
    { value: 34, label: "Bergrechtliche Gewerkschaft" },
    { value: 30, label: "Eingetragene Genossenschaft (eG)" },
    { value: 52, label: "Eingetragener Verein" },
    { value: 36, label: "Europäische Aktiengesellschaft" },
    { value: 28, label: "Europäische wirtschaftliche Interessenvereinigung" },
    { value: 81, label: "Gebietskörperschaft" },
    {
        value: 38,
        label: "Gemeinnützige Gesellschaft mit beschränkter Haftung (gGmbH)",
    },
    { value: 27, label: "Gesellschaft des bürgerlichen Rechts" },
    { value: 35, label: "Gesellschaft mit beschränkter Haftung" },
    { value: 23, label: "GmbH u. Co. KG" },
    { value: 24, label: "GmbH u. Co. OHG" },
    { value: 33, label: "Kolonialgesellschaft" },
    { value: 22, label: "Kommanditgesellschaft" },
    { value: 32, label: "Kommanditgesellschaft auf Aktien" },
    {
        value: 41,
        label: "Kreditgenossenschaft, die Kredite ausschließlich an ihre Mitglieder gewährt",
    },
    {
        value: 43,
        label: "Landwirtschaftl. Nutzungs- oder Verwaltungsgenossenschaft",
    },
    {
        value: 61,
        label: "Nichtrechtsfähiger Verein, Anstalt, Stiftung oder anderes Zweckvermögen",
    },
    { value: 21, label: "Offene Handelsgesellschaft" },
    { value: 82, label: "Öffentl.-rechtl. Religionsgesellschaft" },
    {
        value: 74,
        label: "Öffentl.-rechtl. Versorgungs-, Verkehrs- oder Hafenbetrieb",
    },
    {
        value: 72,
        label: "Öffentliche oder unter Staatsaufsicht stehende Sparkasse",
    },
    {
        value: 16,
        label: "Person mit Beteiligungen an gewerbl. Personengesellschaften",
    },
    { value: 44, label: "Realgemeinde" },
    { value: 45, label: "SCE Europäische Genossenschaft" },
    { value: 99, label: "Sonstige ausländische Rechtsform" },
    { value: 59, label: "Sonstige juristische Person des privaten Rechts" },
    {
        value: 83,
        label: "Sonstige juristische Personen des öffentlichen Rechts",
    },
    { value: 39, label: "Sonstige Kapitalgesellschaft" },
    { value: 73, label: "Sonstige Kreditanstalt des öffentlichen Rechts" },
    {
        value: 29,
        label: "Sonstige Personengesellschaft (z.B. Grundstücksgemeinschaft o.Ä.)",
    },
    {
        value: 79,
        label: "Sonstiger Betrieb gewerblicher Art von jur. Personen des öffentlichen Rechts",
    },
    { value: 71, label: "Staatsbank" },
    { value: 40, label: "Stiftung & Co. KG" },
    { value: 37, label: "Unternehmergesellschaft (haftungsbeschränkt)" },
    { value: 51, label: "Versicherungsverein auf Gegenseitigkeit" },
    {
        value: 42,
        label: "Zentralkasse, die Kredite ausschließlich an ihre Mitglieder gewährt und sich auf genossenschaftl. Aufgaben beschränkt",
    },
];

function TaxPayerLegalForm({
    form,
    onSubmit,
}: {
    form: Form<FiskalyTaxPayerLegalFormType>;
    onSubmit: () => void;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ values, errors }, { setValue, handleSubmit }] = form;

    return (
        <>
            <Headline size="h2">
                {t(
                    "backoffice.common.general_information",
                    "General information"
                )}
            </Headline>
            <InputControl error={errors.companyName}>
                <TextInput
                    required
                    value={values.companyName}
                    label={t("backoffice.common.company_name", "Company Name")}
                    onChangeText={newValue => setValue("companyName", newValue)}
                    maxLength={120}
                />
            </InputControl>
            <InputControl error={errors.legalForm}>
                <Picker
                    required
                    selectedValue={values.legalForm}
                    onValueChange={newValue =>
                        setValue(
                            "legalForm",
                            newValue as SUBMIT_DEcomponents["schemas"]["LegalForm"]
                        )
                    }
                    label={t("backoffice.common.company_form", "Company form")}
                >
                    <Picker.Item
                        key="__select__"
                        value=""
                        label={t("backoffice.common.select", "Select an item")}
                    />
                    {legalForms.map(itr => (
                        <Picker.Item
                            key={`form_${itr.value}`}
                            label={`${itr.label} (${itr.value})`}
                            value={itr.value.toString()}
                        />
                    ))}
                </Picker>
            </InputControl>

            <Divider />
            <Spacer />

            <Headline size="h2">
                {t("backoffice.common.company_address", "Company address")}
            </Headline>
            <View style={styles.inputRow}>
                <InputControl error={errors.street} style={styles.street}>
                    <TextInput
                        required
                        value={values.street}
                        label={t("backoffice.common.street", "Street")}
                        onChangeText={newValue => setValue("street", newValue)}
                        maxLength={72}
                    />
                </InputControl>
                <InputControl
                    error={errors.houseNumber}
                    style={styles.houseNumber}
                >
                    <TextInput
                        required
                        value={values.houseNumber}
                        label={t(
                            "backoffice.common.house_number",
                            "House number"
                        )}
                        onChangeText={newValue =>
                            setValue("houseNumber", newValue)
                        }
                        maxLength={4}
                    />
                </InputControl>
                <InputControl
                    error={errors.houseNumberSuffix}
                    style={styles.houseNumberSuffix}
                >
                    <TextInput
                        label={t("backoffice.common.suffix", "Suffix")}
                        value={values.houseNumberSuffix}
                        onChangeText={newText =>
                            setValue("houseNumberSuffix", newText)
                        }
                        maxLength={20}
                    />
                </InputControl>
            </View>
            <View style={styles.inputRow}>
                <InputControl error={errors.zip} style={styles.zip}>
                    <TextInput
                        required
                        value={values.zip}
                        label={t("backoffice.common.zip", "Zip")}
                        onChangeText={newValue => setValue("zip", newValue)}
                        maxLength={5}
                    />
                </InputControl>
                <InputControl error={errors.town} style={styles.city}>
                    <TextInput
                        required
                        value={values.town}
                        label={t("backoffice.common.city", "City")}
                        onChangeText={newValue => setValue("town", newValue)}
                        maxLength={72}
                    />
                </InputControl>
            </View>

            <InputControl error={errors.webAddress}>
                <TextInput
                    value={values.webAddress}
                    label={t("backoffice.common.web_address", "Web address")}
                    onChangeText={newValue => setValue("webAddress", newValue)}
                    maxLength={1000}
                />
            </InputControl>

            <Divider />
            <Spacer />
            <Headline size="h2">Tax information</Headline>
            <InputControl error={errors.taxNumber}>
                <TextInput
                    required
                    value={values.taxNumber}
                    label={t("backoffice.common.tax_number", "Tax number")}
                    onChangeText={newValue => setValue("taxNumber", newValue)}
                    maxLength={13}
                />
            </InputControl>
            <InputControl error={errors.taxOfficeNumber}>
                <TextInput
                    value={values.taxOfficeNumber}
                    label={t(
                        "backoffice.common.tax_office_number",
                        "Tax office number"
                    )}
                    onChangeText={newValue =>
                        setValue("taxOfficeNumber", newValue)
                    }
                    maxLength={4}
                />
            </InputControl>
            <InputControl error={errors.vatNumber}>
                <TextInput
                    value={values.vatNumber}
                    label={t("backoffice.common.vat_number", "VAT number")}
                    onChangeText={newValue => setValue("vatNumber", newValue)}
                    maxLength={11}
                />
            </InputControl>
            <Divider />
            <Spacer />
            <Button onPress={handleSubmit(onSubmit)} size="small">
                {t("common.save", "Save")}
            </Button>
            <RequiredText />
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    inputRow: {
        ...theme.styles.row,
        columnGap: theme.spacingScale,
    },
    street: {
        ...theme.styles.flex3,
    },
    houseNumber: {
        ...theme.styles.flex2,
    },
    houseNumberSuffix: {
        ...theme.styles.flex1,
    },

    zip: {
        ...theme.styles.flex1,
    },
    city: {
        ...theme.styles.flex3,
    },

    salutation: {
        ...theme.styles.flex1,
    },
    title: {
        ...theme.styles.flex3,
    },
    prefix: {
        ...theme.styles.flex1,
    },
    suffix: {
        ...theme.styles.flex1,
    },
    firstName: { ...theme.styles.flex3 },
    lastName: { ...theme.styles.flex3 },

    birthDate: {
        ...theme.styles.flex1,
    },
    identificationNumber: {
        ...theme.styles.flex1,
    },
});
